/**
 * Football Standings tab – table adapted from platform-tv Standings for virtual-web.
 * Uses the Football 3D schedule (sport_id=50) only, so standings are never fetched for other sports (e.g. horses).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScheduleContext } from 'features/events/context/ScheduleContext';
import { useScheduleStandingsQuery } from 'features/events/queries';
import { useFootball3dScheduleId } from 'features/events/hooks/useFootball3dScheduleId';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { cn } from 'lib/utils';

export default function FootballStandingsTab() {
  const { t } = useTranslation();
  const scheduleId = useFootball3dScheduleId();
  const selectedWeekId = useScheduleContext().selectedWeekId;
  const { data: standings = [], isLoading, isError, refetch, isRefetching } = useScheduleStandingsQuery({
    scheduleId,
    week_id: selectedWeekId ?? undefined,
  });

  if (isError) {
    return (
      <div className="p-4 text-sm text-destructive flex flex-col gap-2">
        <span>{t('ERROR_LOADING_DATA') || 'Failed to load data.'}</span>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isRefetching}
          className="self-start px-3 py-1.5 rounded-md text-xs font-medium bg-destructive/10 text-destructive hover:bg-destructive/20 focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isRefetching ? (t('LOADING') || 'Loading…') : (t('RETRY') || 'Try again')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {t('LOADING') || 'Loading…'}
      </div>
    );
  }

  if (!standings || standings.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        {t('NO_STANDINGS_FOR_WEEK') || 'No standings for this week'}
      </div>
    );
  }

  return (
    <div className="football-standings-tab w-full">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left py-2 px-2 w-12 font-medium text-muted-foreground" scope="col" />
              <th className="text-left py-2 px-2 font-medium text-muted-foreground" scope="col">
                {t('TEAM') || 'Team'}
              </th>
              <th className="text-center py-2 px-2 min-w-[4rem] font-medium text-muted-foreground whitespace-nowrap" scope="col">
                {t('STANDINGS_POINTS') || 'Points'}
              </th>
              <th className="text-center py-2 px-2 min-w-[3rem] font-medium text-muted-foreground whitespace-nowrap" scope="col">
                {t('STANDINGS_WINS') || 'Wins'}
              </th>
              <th className="text-center py-2 px-2 min-w-[3rem] font-medium text-muted-foreground whitespace-nowrap" scope="col">
                {t('STANDINGS_DRAW') || 'Draw'}
              </th>
              <th className="text-center py-2 px-2 min-w-[3.5rem] font-medium text-muted-foreground whitespace-nowrap" scope="col">
                {t('STANDINGS_LOSSES') || 'Losses'}
              </th>
            </tr>
          </thead>
          <tbody>
            {standings
              .filter((team) => team.team_id != null)
              .map((team, index) => {
              const teamCode = team.team_code || '';
              const position = team.position ?? index + 1;
              const points = team.points ?? 0;
              const wins = team.wins ?? 0;
              const draws = team.draws ?? 0;
              const losses = team.losses ?? 0;
              return (
                <tr
                  key={team.team_id}
                  className={cn(
                    'border-b border-border',
                    index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                  )}
                >
                  <td className="py-2 px-2 font-medium text-foreground">{position}°</td>
                  <td className="py-2 px-2">
                    <div className="flex items-center gap-2">
                      <img
                        src={getFootball3dTeamLogoUrl(teamCode)}
                        alt={teamCode}
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          e.target.src = getFootball3dTeamLogoUrl('');
                        }}
                      />
                      <span className="font-medium text-foreground">{teamCode}</span>
                    </div>
                  </td>
                  <td className="text-center py-2 px-2 font-semibold text-foreground">{points}</td>
                  <td className="text-center py-2 px-2 text-foreground">{wins}</td>
                  <td className="text-center py-2 px-2 text-foreground">{draws}</td>
                  <td className="text-center py-2 px-2 text-foreground">{losses}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
